import { Badge, Button, Col, Form, Input, Modal, notification, Row, Tabs } from 'antd';
import {
  FindByTpReferenceAndTpNameParams,
  findByTpReferenceAndTpName,
} from 'services/thirdParty';
import { PlusOutlined, RightOutlined } from '@ant-design/icons';
import React, { useContext, useMemo, useState } from 'react';

import { AuthContext } from 'components/Auth/Auth';
import ButtonLink from 'components/theme_components/ButtonLink';
import DataGrid from 'components/theme_components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import FormInput from 'components/FormInput';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ICar from 'icons/ICar';
import ICorporateBusiness from 'icons/ICorporateBusiness';
import IGroupAlt from 'icons/IGroupAlt';
import ParListSelect from 'components/ParListSelect';
import ResetButtonAction from 'components/theme_components/ResetButtonAction';
import TabPane from 'antd/es/tabs/TabPane';
import Text from 'components/Text';
import { ThirdPartyForProposalType } from 'components/theme_components/GlobalHeader/ThirdPartySearchButton';
import { ThirdPartySearchProps } from 'pages/ThirdParty/TpList';
import Title from 'components/Title';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';

export type ThirdPartySearchModalProps = Pick<
  ThirdPartySearchProps,
  'initialSearchParams' | 'onSelect'
> & {
  visible: boolean;
  onCancel: () => void;
  isProposalCreate: boolean;
  hideThirdPartyModal?: () => void;
  setThirdPartyForProposal?: (
    thirdPartyForProposal: ThirdPartyForProposalType
  ) => void;
  parcodeRole?: string;
  title?: string;
  tpid?: number;
  onlyConc?: boolean;
  onlyPM?: boolean;
  onSelectTp?: (tplist: any[]) => void;
};

const INITIAL_SEARCH_PARAMS: any = {
  parentid: 0,
  role: '',
  tpfiscalid: '',
  tpname: '',
  tpreference: '',
  tpcardid: '',
  tptype: '',
  noparent: false,
  groupcode: '',
  cityorzipcode: '',
  tpparentcompanyname: '',
  tpbrand: '',
  tpsuperparentname: '',
};

const ThirdPartySearchModal = ({
  onCancel,
  visible,
  initialSearchParams,
  onSelect,
  parcodeRole,
  isProposalCreate,
  hideThirdPartyModal,
  setThirdPartyForProposal,
  title,
  tpid,
  onlyConc,
  onlyPM,
  onSelectTp,
}: ThirdPartySearchModalProps) => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] =
    useState<FindByTpReferenceAndTpNameParams>({
      ...INITIAL_SEARCH_PARAMS,
      ...initialSearchParams,
    });

  const { isInspect } = useContext(AuthContext);

  const {
    data: thirdParties,
    refetch,
    isFetching,
  } = useQuery(
    ['findByTpReferenceAndTpName', searchParams],
    () =>
      findByTpReferenceAndTpName(searchParams)
        .then((res) => {
          // Add test on response code
          if (res.code !== '200') {
            throw new Error('Service responded with an error');
          }
          // Filter out items with tpid === sametpid
          return res.list || [];
        })
        .catch(() => {
          return [];
        }),
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );

  const columns_super_company = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'tpname',
        comcode: 'tpnameSPM',
        width: '20%',
      },
      {
        dataIndex: 'tpmainadrcity',
        width: '25%',
        filterComponent: <ParListSelect parcode="ZIPCODE" />,
      },
      {
        comcode: 'limitVIN',
        dataIndex: 'limitVIN',
        width: onlyConc ? 'hide' : '15%',
        render: () => <span>0 %</span>,
      },
      {
        dataIndex: 'limitVo',
        width: onlyConc ? 'hide' : '15%',
        render: () => <span>0 %</span>,
      },
      {
        dataIndex: 'limitVD',
        width: onlyConc ? 'hide' : '15%',
        render: () => <span>0 %</span>,
        
      },
      {
        title: '    ',
        align: 'left',
        render: (val, record) => (
          <RightOutlined
            onClick={() => {
              onCancel();
              navigate(`/thirdparty-sfs/${record.tpid}/${record.tptype}`);
            }}
          />
        ),
        width: onlyConc ? 'hide' : '5%',
        className: 'pr-8',
      },
    ],
    []
  );

  const columns_moral_company = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'parentname',
        width: onlyPM ? 'hide' : '15%',
      },
      {
        dataIndex: 'tpname',
        comcode: 'tpnamePM',
        width: onlyPM ? '200px' : '15%',

      },
      {
        dataIndex: 'tpmainadrcity',
        width: onlyPM ? '200px' : '15%',

      },
      {
        dataIndex: 'tpbrand',
        comcode: 'tpbrandPM',
        width: onlyPM ? '200px' : '20%',


      },
      {
        comcode: 'limitVN',
        dataIndex: 'limitVN',
        width: onlyPM ? 'hide' : '20px',
        render: () => <span>0 %</span>,
      },
      {
        dataIndex: 'limitVo',
        width: onlyPM ? 'hide' : '20px',
        render: () => <span>0 %</span>,
      },
      {
        dataIndex: 'limitVD',
        width: onlyPM ? 'hide' : '20px',
        render: () => <span>0 %</span>,
      },

      {
        title: '    ',
        align: 'left',
        render: (val, record) => (
          <RightOutlined
            onClick={() => {
              onCancel();
              navigate(`/thirdparty-sfs/${record.tpid}/${record.tptype}`);
            }}
          />
        ),
        width: onlyPM ? 'hide' : '20px',
        className: 'pr-8',
      },
    ],
    []
  );

  const columns_concession = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'supertiers',
        comcode: 'supertierconc',
        fixed: 'left',
        width: '15%',
      },
      // {
      //   dataIndex: 'parentname',
      //   comcode: 'parentnameconc',
      //   fixed: 'left',
      //   width: '10%',
      // },
      {
        dataIndex: 'tpreference',
        comcode: 'tpreferenceconc',
        fixed: 'left',
        width: '10%',
      },
      {
        dataIndex: 'tpname',
        comcode: 'tpnameconc',
        fixed: 'left',
        width: '15%',
      },
      {
        dataIndex: 'tpmainadrcity',
        comcode: 'tpmainadrcityconc',
        fixed: 'left',
        width: '15%',
      },
      {
        comcode: 'tpbrandConc',
        dataIndex: 'tpbrand',
        fixed: 'left',
        width: '15%',
      },
      {
        dataIndex: 'limitVIN',
        width: '10%',
        render: () => <span>0 %</span>,
      },
      {
        dataIndex: 'limitVo',
        width: '10%',
        render: () => <span>0 %</span>,
      },
      {
        dataIndex: 'limitVD',
        width: '10%',
        render: () => <span>0 %</span>,
      },
      {
        title: '    ',
        align: 'left',
        width: 100,
        render: (val, record) => (
          <RightOutlined
            onClick={() => {
              onCancel();
              navigate(`/thirdparty-sfs/${record.tpid}/${record.tptype}`);
            }}
          />
        ),
        className: 'pr-8',
      },
    ],
    []
  );

  const [selectedThirdpatyList, setSelectedThirdpatyList] = useState<any[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<any[]>([]);

  const rowSelection = {
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: any) => {
      setSelectedThirdpatyList(selectedRows);
      setSelectedKeys(newSelectedRowKeys);
    },
    selectedRowKeys: selectedKeys,
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        setSearchParams({ ...INITIAL_SEARCH_PARAMS, tptype: 'SPM' });
        onCancel();
      }}
      width={1300}
      destroyOnClose
      footer={null}
      title={<Title level={2} comcode={title ?? 'isearch'} />}
      className="modal-sfs"
    >
      {onlyConc && (
        <>
          <Form
            onValuesChange={(searchinfo) => {
              setSearchParams({ ...searchParams, ...searchinfo });
            }}
            layout="vertical"
          >
            <Row gutter={6} justify="space-between">
              <FormInput col md={5} name="tpname" nolabel>
                <Input
                  className="ut-input-search ut-input-search-TP-parent"
                  name="tpname"
                />
              </FormInput>
              {/* <FormInput col md={5} name="groupcode" nolabel>
                <Input
                  className="ut-input-search ut-input-search-TP-parent"
                  name="tpfiscalid"
                />
              </FormInput> */}

              <FormInput col md={5} name="tpfiscalid" nolabel>
                <Input
                  className="ut-input-search ut-input-search-TP-parent"
                  name="tpfiscalid"
                />
              </FormInput>

              <FormInput col md={5} name="cityorzipcode" nolabel>
                <Input className='ut-input-search ut-input-search-TP-parent' name="cityorzipcode" placeholder="City or zipcode" />
              </FormInput>

              <Col>
                <ResetButtonAction
                  comcode="Reset"
                  className="ut-reset-btn"
                  type="default"
                  onClick={() => {
                    setSearchParams({
                      ...INITIAL_SEARCH_PARAMS,
                      tptype: 'CONC',
                    });
                    refetch();
                  }}
                ></ResetButtonAction>
              </Col>
            </Row>
          </Form>

          <DataGrid
            dataSource={thirdParties}
            columns={columns_concession}
            rowKey={(record) => record.tpid}
            hideFilters
            scroll={{ x: 1000, y: 360 }}
            rowSelection={{ type: 'checkbox', ...rowSelection }}
            isLoading={isFetching}
            scrollInput={{ x: 1000, y: 550 }}

          />

          <Row justify="end" style={{ marginTop: 10 }}>
            <ButtonLink
              htmlType="button"
              comcode="cancel"
              iconPosition="end"
              onClick={onCancel}
            />
            <ButtonLink
              htmlType="button"
              type="primary"
              onClick={() => {
                if (!isInspect && onSelectTp) {
                  if (selectedThirdpatyList.length === 0) {
                    notification.warning({
                      message: 'Veuillez sélectionner au moins un tiers moral'
                    });
                    return;
                  }
                  onSelectTp(selectedThirdpatyList);
                }
              }}
              comcode="add"
              iconPosition="end"
              className="ml-10"
            />
          </Row>
        </>
      )}

      {onlyPM && (
        <>
          <Form
            onValuesChange={(searchinfo) => {
              setSearchParams({ ...searchParams, ...searchinfo });
            }}
            layout="vertical"
          >
            <Row gutter={6} justify="space-between">
              <FormInput col md={5} name="tpname" nolabel>
                <Input
                  className="ut-input-search ut-input-search-TP-parent"
                  name="tpname"
                />
              </FormInput>
              {/* <FormInput col md={5} name="groupcode" nolabel>
                <Input
                  className="ut-input-search ut-input-search-TP-parent"
                  name="tpfiscalid"
                />
              </FormInput> */}

              <FormInput col md={5} name="tpfiscalid" nolabel>
                <Input
                  className="ut-input-search ut-input-search-TP-parent"
                  name="tpfiscalid"
                />
              </FormInput>

              <FormInput col md={5} name="cityorzipcode" nolabel>
                <Input className='ut-input-search ut-input-search-TP-parent' name="cityorzipcode" placeholder="City or zipcode" />
              </FormInput>

              <Col>
                <ResetButtonAction
                  comcode="Reset"
                  className="ut-reset-btn"
                  type="default"
                  onClick={() => {
                    setSearchParams({
                      ...INITIAL_SEARCH_PARAMS,
                      tptype: 'SPM',
                    });
                    refetch();
                  }}
                ></ResetButtonAction>
              </Col>
            </Row>
          </Form>

          <DataGrid
            dataSource={thirdParties}
            columns={columns_moral_company}
            rowKey={(record) => record.tpid}
            scroll={{ x: 1000, y: 360 }}
           // pagination={{ pageSize: 10, align: 'center' }}
            isLoading={isFetching}
            rowSelection={{ type: 'checkbox', ...rowSelection }}
            scrollInput={{ x: 1000, y: 550 }}

          />

          <Row justify="end" style={{ marginTop: 10 }}>
            <ButtonLink htmlType="button" comcode="cancel" iconPosition="end" onClick={onCancel} />
            <ButtonLink
              htmlType="button"
              type="primary"
              onClick={() => {
                if (!isInspect && onSelectTp) {
                  if (selectedThirdpatyList.length === 0) {
                    notification.warning({
                      message: 'Veuillez sélectionner au moins un tiers moral'
                    });
                    return;
                  }
                  onSelectTp(selectedThirdpatyList);
                }
              }}
              comcode="add"
              iconPosition="end"
              className="ml-10"
            />
          </Row>
        </>
      )}

      {!onlyPM && !onlyConc && (
        <Tabs
          // style={{ height: '800px' }}
          tabBarExtraContent={
            <p
              className="addlink"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (!isInspect) {
                  navigate('/thirdparty-sfs/create');
                  onCancel();
                }
              }}
            >
              <PlusOutlined style={{ marginRight: 7 }} />
              <GetLabel label="super-tiers-moral" />
            </p>
          }
          onChange={(key) => {
            setSearchParams({ ...INITIAL_SEARCH_PARAMS, tptype: key });
          }}
          activeKey={searchParams.tptype}
        >
          <TabPane
            forceRender
            tab={
              <div
                className={`ant-tabs-title ant-tabs-title-${searchParams.tptype === 'SPM' ? 'active' : 'blue'
                  }`}
              >
                <ICorporateBusiness
                  color={searchParams.tptype === 'SPM' ? '#DD7248' : '#243784'}
                />
                <GetLabel label="supertiers" />
              </div>
            }
            key="SPM"
          >
            <Form
              onValuesChange={(searchinfo) => {
                setSearchParams({ ...searchParams, ...searchinfo });
              }}
              style={{ marginTop: 30, padding: '0px 5px' }}
              layout="vertical"
            >
              <Row gutter={[10, 10]} justify="space-between">
                <FormInput
                  col
                  md={10}
                  name="tpname"
                  comcode="tpnamespm"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>
                {/* <FormInput
                  col
                  md={5}
                  name="tpreference"
                  comcode="groupcodespm"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput> */}

                {/* <FormInput
                  col
                  md={5}
                  name="tpfiscalid"
                  comcode="tpfiscalidspm"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput> */}

                <FormInput col md={10} name="cityorzipcode" nolabel>
                  <Input className='ut-input-search ut-input-search-TP-parent' name="cityorzipcode" placeholder="City or zipcode" />
                </FormInput>

                <Col>
                  <ResetButtonAction
                    comcode="Reset"
                    className="ut-reset-btn"
                    type="default"
                    onClick={() => {
                      setSearchParams({
                        ...INITIAL_SEARCH_PARAMS,
                        tptype: 'SPM',
                      });
                      refetch();
                    }}
                  ></ResetButtonAction>
                </Col>
              </Row>
            </Form>

            <DataGrid
              dataSource={thirdParties}
              columns={columns_super_company}
              rowKey={(record) => record.tpid}
              scroll={{ x: 1000, y: 360 }}
             // pagination={{ pageSize: 10 }}
              hideFilters
              isLoading={isFetching}
              hideTitle
              scrollInput={{ x: 1000, y: 550 }}

            />
          </TabPane>
          <TabPane
            forceRender
            tab={
              <div
                className={`ant-tabs-title ant-tabs-title-${searchParams.tptype === 'PM' ? 'active' : 'duck-blue'
                  }`}
              >
                <IGroupAlt
                  color={searchParams.tptype === 'PM' ? '#DD7248' : '#0E4468'}
                />
                <GetLabel label="moralthirdparty" />
              </div>
            }
            key="PM"
          >
            <Form
              onValuesChange={(searchinfo) => {
                setSearchParams({ ...searchParams, ...searchinfo });
              }}
              style={{ marginTop: 30, padding: '0px 5px' }}
              layout="vertical"
            >
              <Row gutter={6} justify="space-between">
                <FormInput
                  col
                  md={5}
                  name="tpparentcompanyname"
                  comcode="tpparentcompanynamePM"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>
                <FormInput col md={5} name="tpname" comcode="tpnamePM" nolabel>
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>

                <FormInput
                  col
                  md={5}
                  name="tpfiscalid"
                  comcode="legalIDPM"
                  nolabel
                >
                  <Input
                    className="ut-input-search ut-input-search-TP-parent"
                    name="tpfiscalid"
                  />
                </FormInput>

                <FormInput col md={5} name="cityorzipcode" nolabel>
                  <Input className='ut-input-search ut-input-search-TP-parent' name="cityorzipcode" placeholder="City or zipcode" />

                </FormInput>

                <Col>
                  <ResetButtonAction
                    comcode="Reset"
                    className="ut-reset-btn"
                    type="default"
                    onClick={() => {
                      setSearchParams({
                        ...INITIAL_SEARCH_PARAMS,
                        tptype: 'PM',
                      });
                      refetch();
                    }}
                  ></ResetButtonAction>
                </Col>
              </Row>
            </Form>

            <DataGrid
              hideFilters
              dataSource={thirdParties}
              columns={columns_moral_company}
              rowKey={(record) => record.tpid}
              scroll={{ x: 1000, y: 360 }}
             // pagination={{ pageSize: 10 }}
              isLoading={isFetching}
              hideTitle
              scrollInput={{ x: 1000, y: 550 }}

            />
          </TabPane>
          <TabPane
            forceRender
            tab={
              <div
                className={`ant-tabs-title ant-tabs-title-${searchParams.tptype === 'CONC' ? 'active' : 'green'
                  }`}
              >
                <ICar
                  color={searchParams.tptype === 'CONC' ? '#DD7248' : '#20635D'}
                />
                <GetLabel label="conccession" />
              </div>
            }
            key="CONC"
          >
            <Form
              onValuesChange={(searchinfo) => {
                setSearchParams({ ...searchParams, ...searchinfo });
              }}
              layout="vertical"
              style={{ marginTop: 30, padding: '0px 5px' }}
            >
              <Row gutter={6} justify="space-between">
                {/* <FormInput
                  col
                  md={3}
                  name="groupcode"
                  comcode="groupcodeCONC"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput> */}
                <FormInput
                  col
                  md={3}
                  name="tpsuperparentname"
                  comcode="tpsuperparentname"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>
                <FormInput
                  col
                  md={3}
                  name="tpparentcompanyname"
                  comcode="tpparentcompanynameCONC"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>

                <FormInput
                  col
                  md={3}
                  name="tpreference"
                  comcode="tpreferenceCONC"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>

                <FormInput
                  col
                  md={3}
                  name="tpname"
                  comcode="tpnameCONC"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>

                <FormInput
                  col
                  md={3}
                  name="tpfiscalid"
                  comcode="tpfiscalidconc"
                  nolabel
                >
                  <Input className="ut-input-search ut-input-search-TP-parent" />
                </FormInput>

                <FormInput col md={3} name="cityorzipcode" nolabel>
                  <Input className='ut-input-search ut-input-search-TP-parent' name="cityorzipcode" placeholder="City or zipcode" />
                </FormInput>

                <FormInput col md={3} name="tpbrand" nolabel>
                  <ParListSelect
                    parcode="TPBRAND"
                    className="ut-input-search ut-input-search-TP-parent"
                  />
                </FormInput>

                <Col>
                  <ResetButtonAction
                    comcode="Reset"
                    type="default"
                    className="ut-reset-btn"
                    onClick={() => {
                      setSearchParams({
                        ...INITIAL_SEARCH_PARAMS,
                        tptype: 'CONC',
                      });
                      refetch();
                    }}
                  ></ResetButtonAction>
                </Col>
              </Row>
            </Form>

            <DataGrid
              hideFilters
              dataSource={thirdParties}
              columns={columns_concession}
              rowKey={(record) => record.tpid}
              scroll={{ x: 1000, y: 360 }}
              //pagination={{ pageSize: 10 }}
              isLoading={isFetching}
              hideTitle
              scrollInput={{ x: 1000, y: 550 }}

            />
          </TabPane>
        </Tabs>
      )}
    </Modal>
  );
};

export default ThirdPartySearchModal;
